'use client';

import { c as _c } from "react/compiler-runtime";
import { Divider, Stack, Typography } from '@mui/material';
import type { ClassificationCategory, Condition, DataClassificationEntityOption } from '@repo/api-gw-sdk';
import { useMemo } from 'react';
import { ConditionPanelPopover } from '@/components/conditions/conditionPanelPopover';
import { ConditionSelectionPanel } from '@/components/conditions/conditionSelectionPanel';
import { useConditionPanel } from '@/components/conditions/useConditionPanel';
import { CheckboxTreeFilter, type CheckboxTreeGroup } from '@/components/filters/checkboxTreeFilter';
import { HelpIcon } from '@/components/shared/helpIcon';
import { Loader } from '@/components/shared/loader';
import { useDAL } from '@/contexts/useDAL';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { PiiStrings } from '@/data/inventory/data';
import { dataClassLabel } from '@/data/inventory/dataClassLabels';
import { useClassificationCategories, useIsDataClassRolledOut } from '@/data/inventory/useClassificationCategories';
import { conditionValues, createValueCondition, findValueCondition, isValueCondition, type CompanionConditionComponentProps } from '@/types/advanceFilter';
import { MultiSelectFilter, type FilterProps } from './filters';
const DATA_CLASSES = 'dataClasses';
const DATA_CLASS_ENTITIES = 'dataClassEntities';
const OTHER_CATEGORY = 'Other';
const DEFAULT_CLASS_NAMES = ['PII', 'FI', 'PHI', 'CREDENTIALS'];
const DEFAULT_CLASS_ORDER = new Map(DEFAULT_CLASS_NAMES.map((name, index) => [name, index]));
const isDefaultClass = (name: string) => DEFAULT_CLASS_NAMES.includes(name);
const isCustomClass = (name: string) => !isDefaultClass(name) && name !== OTHER_CATEGORY;
const sortDefaultClasses = <T extends {
  id: string;
},>(classes: T[]) => [...classes].sort((a, b) => (DEFAULT_CLASS_ORDER.get(a.id) ?? DEFAULT_CLASS_NAMES.length) - (DEFAULT_CLASS_ORDER.get(b.id) ?? DEFAULT_CLASS_NAMES.length));
interface DataClassGroup {
  id: string;
  description?: string;
  children: string[];
}
const buildGroups = (categories: ClassificationCategory[], entities: DataClassificationEntityOption[]): DataClassGroup[] => {
  const groups = new Map<string, DataClassGroup>(categories.map(c => [c.name, {
    id: c.name,
    description: c.description,
    children: []
  }]));
  for (const entity of entities) {
    const cats = entity.categories.length ? entity.categories : [OTHER_CATEGORY];
    for (const cat of cats) {
      const group = groups.get(cat) ?? {
        id: cat,
        children: []
      };
      group.children.push(entity.name);
      groups.set(cat, group);
    }
  }
  return [...groups.values()].map(group => ({
    ...group,
    children: [...new Set(group.children)].sort()
  }));
};
const isDataClassCondition = (condition: Condition) => isValueCondition(condition) && (condition.property === DATA_CLASSES || condition.property === DATA_CLASS_ENTITIES);
const buildDataClassConditions = (classes: string[], entities: string[]): Condition[] => {
  const out: Condition[] = [];
  if (classes.length) {
    out.push(createValueCondition(DATA_CLASSES, classes));
  }
  if (entities.length) {
    out.push(createValueCondition(DATA_CLASS_ENTITIES, entities));
  }
  return out;
};
const stripDataClassConditions = (conditions: Condition[]): Condition[] => conditions.filter(c => !isDataClassCondition(c));
const SECTION_HEADER_PADDING = '8px 0px';
export const entityLabel = (entity: string) => PiiStrings[entity] ?? entity;
const CUSTOM_CLASS_SUBTITLE = 'Manually added by Eon for your organization';
const CustomClassTooltip = t0 => {
  const $ = _c(5);
  const {
    description
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Divider sx={{
      borderColor: "currentColor",
      opacity: 0.2
    }} />;
    t2 = {
      gap: "8px"
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Stack direction="row" sx={{
      gap: "8px",
      alignItems: "center"
    }}><i className="material-symbols-settings-outline-rounded text-[24px]" />{CUSTOM_CLASS_SUBTITLE}</Stack>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== description) {
    t4 = <Stack divider={t1} sx={t2}>{description}{t3}</Stack>;
    $[3] = description;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
};
const DataClassesWithEntitiesFilter = ({
  conditions,
  onChange
}: FilterProps) => {
  const dal = useDAL();
  const {
    categories,
    isLoading: categoriesLoading
  } = useClassificationCategories();
  const {
    data: body,
    isLoading: entitiesLoading
  } = dal.inventory.entities.useList();
  const isRolledOut = useIsDataClassRolledOut();

  // An entity whose every category is hidden by the rollout flag must not
  // resurrect that class's group: buildGroups seeds groups from the already-
  // filtered categories, but falls back to `groups.get(cat) ?? { id: cat, ... }`
  // for any category an entity references, which would otherwise re-create it.
  const visibleEntities = useMemo(() => (body?.entities ?? []).filter(entity => entity.categories.length === 0 || entity.categories.some(isRolledOut)), [body?.entities, isRolledOut]);
  const groups = useMemo(() => buildGroups(categories, visibleEntities), [categories, visibleEntities]);
  if (categoriesLoading || entitiesLoading) {
    return <Loader />;
  }
  const selectedClasses = conditionValues(findValueCondition(conditions, DATA_CLASSES));
  const selectedEntities = conditionValues(findValueCondition(conditions, DATA_CLASS_ENTITIES));
  const apply = (classes: string[], entities: string[]) => {
    onChange([...stripDataClassConditions(conditions), ...buildDataClassConditions(classes, entities)]);
  };
  const onParentChange = (group: DataClassGroup) => {
    const isOn = selectedClasses.includes(group.id);
    const nextClasses = isOn ? selectedClasses.filter(c => c !== group.id) : [...selectedClasses, group.id];
    const nextEntities = selectedEntities.filter(e => !group.children.includes(e));
    apply(nextClasses, nextEntities);
  };
  const onChildChange = (group_0: DataClassGroup, entity_0: string) => {
    const nextClasses_0 = selectedClasses.filter(c_0 => c_0 !== group_0.id);
    const otherEntities = selectedEntities.filter(e_0 => !group_0.children.includes(e_0));
    // Selecting a child while the parent class is selected swaps the broad
    // class for that single entity in the same group.
    if (selectedClasses.includes(group_0.id)) {
      apply(nextClasses_0, [...otherEntities, entity_0]);
      return;
    }
    const nextEntities_0 = selectedEntities.includes(entity_0) ? selectedEntities.filter(e_1 => e_1 !== entity_0) : [...selectedEntities, entity_0];
    apply(nextClasses_0, nextEntities_0);
  };
  const byId = new Map(groups.map(group_1 => [group_1.id, group_1]));
  const toTreeGroups = (sectionGroups: DataClassGroup[], isCustom: boolean): CheckboxTreeGroup[] => sectionGroups.map(group_2 => ({
    id: group_2.id,
    label: dataClassLabel(group_2.id),
    selected: selectedClasses.includes(group_2.id),
    trailing: <HelpIcon title={isCustom ? <CustomClassTooltip description={group_2.description} /> : group_2.description ?? group_2.id} className='cursor-default' tooltipProps={{
      slotProps: {
        tooltip: {
          sx: {
            padding: '16px'
          }
        }
      }
    }} />,
    children: group_2.children.map(entity_1 => ({
      id: entity_1,
      label: entityLabel(entity_1),
      selected: selectedEntities.includes(entity_1)
    }))
  }));
  const hasCustoms = categories.some(c_1 => isCustomClass(c_1.name));
  const defaultGroups = sortDefaultClasses(groups.filter(g => isDefaultClass(g.id)));
  const customGroups = groups.filter(g_0 => isCustomClass(g_0.id));
  const otherGroups = groups.filter(g_1 => !isDefaultClass(g_1.id) && !isCustomClass(g_1.id));
  return <Stack sx={{
    gap: '0px'
  }}>
      {hasCustoms && <Typography variant='overline' sx={{
      padding: SECTION_HEADER_PADDING
    }}>
          Default classes
        </Typography>}
      <CheckboxTreeFilter groupTestIdPrefix='filter-dataClasses-' childTestIdPrefix='filter-dataClassEntities-' groups={toTreeGroups(defaultGroups, false)} onGroupToggle={groupId => onParentChange(byId.get(groupId)!)} onChildToggle={(groupId_0, entity_2) => onChildChange(byId.get(groupId_0)!, entity_2)} />
      {hasCustoms && <>
          <Typography variant='overline' sx={{
        padding: SECTION_HEADER_PADDING
      }}>
            Custom classes
          </Typography>
          <CheckboxTreeFilter groupTestIdPrefix='filter-dataClasses-' childTestIdPrefix='filter-dataClassEntities-' groups={toTreeGroups(customGroups, true)} onGroupToggle={groupId_1 => onParentChange(byId.get(groupId_1)!)} onChildToggle={(groupId_2, entity_3) => onChildChange(byId.get(groupId_2)!, entity_3)} />
        </>}
      {otherGroups.length > 0 && <CheckboxTreeFilter groupTestIdPrefix='filter-dataClasses-' childTestIdPrefix='filter-dataClassEntities-' groups={toTreeGroups(otherGroups, false)} onGroupToggle={groupId_3 => onParentChange(byId.get(groupId_3)!)} onChildToggle={(groupId_4, entity_4) => onChildChange(byId.get(groupId_4)!, entity_4)} />}
    </Stack>;
};
const FlatDataClassesFilter = props => {
  const $ = _c(8);
  const {
    categories,
    isLoading
  } = useClassificationCategories();
  let t0;
  if ($[0] !== categories) {
    bb0: {
      const toOption = _temp;
      const defaults = categories.filter(_temp2).map(toOption);
      const sortedDefaults = sortDefaultClasses(defaults);
      const customs = categories.filter(_temp3).map(toOption);
      if (customs.length === 0) {
        t0 = sortedDefaults;
        break bb0;
      }
      let t1;
      if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = {
          id: "__default_header",
          name: "Default classes",
          isGroupTitle: true
        };
        $[2] = t1;
      } else {
        t1 = $[2];
      }
      let t2;
      if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = {
          id: "__custom_header",
          name: "Custom classes",
          isGroupTitle: true
        };
        $[3] = t2;
      } else {
        t2 = $[3];
      }
      t0 = [t1, ...sortedDefaults, t2, ...customs];
    }
    $[0] = categories;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const options = t0;
  if (isLoading) {
    let t1;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Loader />;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    return t1;
  }
  let t1;
  if ($[5] !== options || $[6] !== props) {
    t1 = <MultiSelectFilter {...props} options={options} />;
    $[5] = options;
    $[6] = props;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
};
export const DataClassesFilter = props => {
  const $ = _c(3);
  const {
    EnableInventoryEntitiesFilter
  } = useFeatureFlags();
  let t0;
  if ($[0] !== EnableInventoryEntitiesFilter || $[1] !== props) {
    t0 = EnableInventoryEntitiesFilter === true ? <DataClassesWithEntitiesFilter {...props} /> : <FlatDataClassesFilter {...props} />;
    $[0] = EnableInventoryEntitiesFilter;
    $[1] = props;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};

/**
 * The filter bar's data-classes chip. Renders the same filter the accordion
 * panel does — class groups, entity children and all — inside the popover the
 * chip anchors, so both surfaces stay one implementation. It needs the chip's
 * whole condition set because ticking a class writes `dataClasses` while
 * ticking an entity inside it writes `dataClassEntities`.
 */
export const DataClassesCondition = t0 => {
  const $ = _c(13);
  const {
    property,
    conditions,
    onChange,
    panelHost
  } = t0;
  const panel = useConditionPanel(panelHost);
  let t1;
  if ($[0] !== conditions || $[1] !== onChange || $[2] !== property) {
    t1 = <Stack className="overflow-y-auto px-[24px] py-[12px]"><DataClassesFilter property={property} conditions={conditions} onChange={onChange} /></Stack>;
    $[0] = conditions;
    $[1] = onChange;
    $[2] = property;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== conditions || $[5] !== onChange || $[6] !== t1) {
    t2 = <Stack className="w-[308px]"><ConditionSelectionPanel conditions={conditions} onConditionsChange={onChange} entityName="data class" labelOf={_temp4}>{t1}</ConditionSelectionPanel></Stack>;
    $[4] = conditions;
    $[5] = onChange;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== panel.anchorEl || $[9] !== panel.close || $[10] !== panel.isOpen || $[11] !== t2) {
    t3 = <ConditionPanelPopover open={panel.isOpen} anchorEl={panel.anchorEl} onClose={panel.close}>{t2}</ConditionPanelPopover>;
    $[8] = panel.anchorEl;
    $[9] = panel.close;
    $[10] = panel.isOpen;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
};
function _temp(c) {
  return {
    id: c.name,
    name: dataClassLabel(c.name),
    description: isCustomClass(c.name) ? <CustomClassTooltip description={c.description} /> : c.description
  };
}
function _temp2(c_0) {
  return isDefaultClass(c_0.name);
}
function _temp3(c_1) {
  return isCustomClass(c_1.name);
}
function _temp4(t0) {
  const {
    property: name,
    value
  } = t0;
  return name === DATA_CLASS_ENTITIES ? entityLabel(value) : dataClassLabel(value);
}